/**
 * Deliverable validation schemas using Zod
 */

import { z } from 'zod';

/**
 * Date/datetime validation that accepts:
 * - ISO 8601 datetime: "2024-01-15T00:00:00.000Z"
 * - Date only: "2024-01-15" (will be transformed to ISO datetime at midnight UTC)
 */
const dateOrDatetime = z
  .string()
  .refine(
    (val) => {
      // Accept ISO 8601 datetime or YYYY-MM-DD date format
      const isoDatetime = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d{3})?Z?$/;
      const dateOnly = /^\d{4}-\d{2}-\d{2}$/;
      return isoDatetime.test(val) || dateOnly.test(val);
    },
    { message: 'Invalid date format. Expected YYYY-MM-DD or ISO 8601 datetime' }
  )
  .transform((val) => {
    // If it's just a date, convert to ISO datetime at midnight UTC
    if (/^\d{4}-\d{2}-\d{2}$/.test(val)) {
      return `${val}T00:00:00.000Z`;
    }
    // If it doesn't end with Z, add it
    if (!val.endsWith('Z')) {
      return `${val}Z`;
    }
    return val;
  });

export const CreateDeliverableSchema = z.object({
  dealId: z.string({ required_error: 'Deal ID is required' }).min(1, 'Deal ID is required'),
  description: z
    .string({ required_error: 'Description is required' })
    .min(1, 'Description is required')
    .max(500, 'Description must be at most 500 characters'),
  platform: z.enum([
    'instagram',
    'tiktok',
    'youtube',
    'twitter',
    'facebook',
    'linkedin',
    'blog',
    'other',
  ]),
  contentType: z.enum([
    'post',
    'story',
    'reel',
    'video',
    'article',
    'tweet',
    'carousel',
    'live',
    'other',
  ]),
  quantity: z.number().int().min(1).max(100).default(1),
  dueDate: dateOrDatetime.optional(),
  notes: z.string().max(1000).optional(),
});

export const UpdateDeliverableSchema = z.object({
  description: z
    .string()
    .min(1, 'Description is required')
    .max(500, 'Description must be at most 500 characters')
    .optional(),
  platform: z
    .enum([
      'instagram',
      'tiktok',
      'youtube',
      'twitter',
      'facebook',
      'linkedin',
      'blog',
      'other',
    ])
    .optional(),
  contentType: z
    .enum([
      'post',
      'story',
      'reel',
      'video',
      'article',
      'tweet',
      'carousel',
      'live',
      'other',
    ])
    .optional(),
  quantity: z.number().int().min(1).max(100).optional(),
  dueDate: dateOrDatetime.optional(),
  status: z
    .enum([
      'pending',
      'in_progress',
      'review',
      'revision',
      'approved',
      'completed',
      'cancelled',
    ])
    .optional(),
  notes: z.string().max(1000).optional(),
});

export const CompleteDeliverableSchema = z.object({
  notes: z.string().max(1000).optional(),
  attachments: z.array(z.string().url()).optional(),
});
